import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import OperatorLayout from '@/layouts/OperatorLayout'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ArrowLeft, ArrowRight, BedDouble, Plus, X } from 'lucide-react'
import type { RoomHistoryProps, RoomInventoryEra, RoomInventoryHistory } from '@/types/shared'

/**
 * "Room count history" — the standalone page behind Rooms Setup's button of the same name.
 * Journey 3 of docs/plans/2026-08-22-inventory-truth-tracking.md §5, now with the write half:
 * the operator can ADD a point ("from this month, we had N rooms") and REMOVE one their team
 * added, because they know their hotel's past better than any inference of ours.
 *
 * MONTH GRANULARITY ONLY (design D4). The boundary date is stored day-precise and rendered
 * "Apr 2026" — operators remember "we opened the basement in April", never "on the 14th", and a
 * day would claim a precision the inference does not have. The add dialog asks for a month for
 * the same reason; the backend receives the first of it.
 *
 * VOCABULARY IS LOCKED (design D8): "bookable rooms" / "room count". The words *inventory*,
 * *era*, *baseline*, *denominator* and *estimator* never reach an operator — they stay in the
 * internal names below, in admin, and in the design doc.
 *
 * NO MATH AND NO CLIENT RULES: every count renders exactly as the backend sent it, and the only
 * client-side check on the form is presence. The server owns every rule — including the evidence
 * floor ("cannot be below N — that many rooms were sold on a single night in this period"), whose
 * verdict lands in `errors.roomHistory` and renders inside the dialog.
 */

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/**
 * 'YYYY-MM-DD' → 'Apr 2026'. Parsed off the string, NOT through `new Date`: a date-only ISO
 * string parses as UTC midnight, so west of Greenwich `toLocaleDateString` would render the
 * month before the one the backend meant.
 */
export function monthLabel(effectiveFrom: string): string {
  const parts = /^(\d{4})-(\d{2})/.exec(effectiveFrom)
  if (!parts) return effectiveFrom
  const name = MONTH_NAMES[Number(parts[2]) - 1]
  return name ? `${name} ${parts[1]}` : effectiveFrom
}

function roomWord(units: number): string {
  return units === 1 ? 'room' : 'rooms'
}

/**
 * A room only has a timeline once there are TWO counts to draw a boundary between: the first
 * count is where the room started, and the arrow needs a month and a second count to point at.
 * One count (or none) means the room has never been observed to change.
 */
function hasTimeline(room: RoomInventoryHistory): boolean {
  return room.eras.length >= 2
}

/** The dialog's month options: '01'..'12', full names — distinct from the timeline's short form. */
const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]

/** Current year down to −3: far enough for "we opened the basement two summers ago". */
function yearOptions(): string[] {
  const thisYear = new Date().getFullYear()
  return [0, 1, 2, 3].map((back) => String(thisYear - back))
}

export default function RoomHistory({
  currentUser,
  property,
  properties,
  rooms,
}: RoomHistoryProps) {
  const [dialogRoom, setDialogRoom] = useState<RoomInventoryHistory | null>(null)

  return (
    <OperatorLayout
      title="Room count history"
      currentUser={currentUser}
      property={property}
      properties={properties}
    >
      <div className="space-y-6">
        {/* Back to the page this one is layered behind. */}
        <Link
          href={`${property.basePath}/pricing-rules/rooms-setup`}
          className="animate-slide-in inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
          style={{ animationDelay: '0ms' }}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Rooms Setup
        </Link>

        <div className="animate-slide-in" style={{ animationDelay: '50ms' }}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Room count history</h1>
            <HelpTooltip
              content="How many bookable rooms each of your rooms has had over time. Changes your PMS reports are recorded as they happen; where your PMS keeps no history, we work the change out from your own bookings — and you can add a change yourself if you know one we missed. Shown by month, because that is the accuracy the bookings support."
              learnMore="/guides/base-rates"
            />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            What each room&apos;s bookable count was, and when it changed. The engine uses this to
            read your history fairly.
          </p>
        </div>

        {rooms.length > 0 ? (
          <Card
            className="animate-slide-in overflow-hidden rounded-xl border-border/60 bg-white shadow-sm"
            style={{ animationDelay: '100ms' }}
          >
            <CardContent className="p-0">
              <div className="divide-y divide-border/40">
                {rooms.map((room) => (
                  <RoomHistoryRow
                    key={room.roomTypeId}
                    room={room}
                    basePath={property.basePath}
                    onAddChange={() => setDialogRoom(room)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div
            className="animate-slide-in rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm"
            style={{ animationDelay: '100ms' }}
          >
            <BedDouble className="mx-auto h-8 w-8 text-muted-foreground/40" />
            <p className="mt-3 text-sm font-medium text-foreground">No room types found</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Sync this property&apos;s rooms from your PMS on Rooms Setup first.
            </p>
          </div>
        )}
      </div>

      <AddChangeDialog
        room={dialogRoom}
        basePath={property.basePath}
        onClose={() => setDialogRoom(null)}
      />
    </OperatorLayout>
  )
}

function RoomHistoryRow({
  room,
  basePath,
  onAddChange,
}: {
  room: RoomInventoryHistory
  basePath: string
  onAddChange: () => void
}) {
  return (
    <div className="space-y-2 px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 truncate text-sm font-medium text-foreground">{room.roomTypeName}</p>
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            <span className="font-semibold tabular-nums text-foreground">{room.currentUnits}</span>{' '}
            {roomWord(room.currentUnits)} today
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground"
            onClick={onAddChange}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add a change
          </Button>
        </div>
      </div>

      {hasTimeline(room) ? (
        <Timeline room={room} basePath={basePath} />
      ) : (
        // NOT an empty state and NOT an error — this is the ordinary case for most rooms, so it
        // reads as a plain fact rather than a gap in the product.
        <p className="text-xs text-muted-foreground">No changes recorded</p>
      )}
    </div>
  )
}

/** `18 rooms → Apr 2026 → 24 rooms`. Wraps rather than scrolls — a room can have several steps. */
function Timeline({ room, basePath }: { room: RoomInventoryHistory; basePath: string }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
      {room.eras.map((era, index) => (
        <React.Fragment key={era.id}>
          {index > 0 && (
            <>
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" aria-hidden />
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                {monthLabel(era.effectiveFrom)}
              </span>
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" aria-hidden />
            </>
          )}
          <TimelineEntry era={era} basePath={basePath} />
        </React.Fragment>
      ))}
    </div>
  )
}

function TimelineEntry({ era, basePath }: { era: RoomInventoryEra; basePath: string }) {
  // A form action on the current page, not navigation — router.delete is correct here.
  const remove = () => {
    if (!window.confirm('Remove this entry? Your prices will refresh.')) return
    router.delete(`${basePath}/rooms/history/${era.id}`, { preserveScroll: true })
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-sm font-semibold tabular-nums text-foreground">{era.totalUnits}</span>
      <span className="text-sm text-muted-foreground">{roomWord(era.totalUnits)}</span>
      {/* Says whose claim this is. A count we watched change is a fact; a count we worked out
          from bookings is our reading of one; a count the operator's team set is theirs — and
          only theirs can be removed here. */}
      {era.source === 'inferred' && (
        <Badge
          variant="secondary"
          className="border-transparent bg-slate-100 px-2 py-0 text-[11px] font-medium text-slate-600"
        >
          estimated from your bookings
        </Badge>
      )}
      {era.source === 'operator' && (
        <>
          <Badge
            variant="secondary"
            className="border-transparent bg-slate-100 px-2 py-0 text-[11px] font-medium text-slate-600"
          >
            set by your team
          </Badge>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 rounded-lg p-0 text-rose-500 hover:text-rose-600"
            aria-label="Remove this entry"
            onClick={remove}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </>
      )}
    </span>
  )
}

function AddChangeDialog({
  room,
  basePath,
  onClose,
}: {
  room: RoomInventoryHistory | null
  basePath: string
  onClose: () => void
}) {
  const [month, setMonth] = useState('')
  const [year, setYear] = useState('')
  const [count, setCount] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Reset on every close, so a rejected entry for one room never leaks into the next room's
  // dialog.
  const reset = () => {
    setMonth('')
    setYear('')
    setCount('')
    setError(null)
  }

  const close = () => {
    reset()
    onClose()
  }

  const submit = () => {
    if (!room) return
    // Presence only — the server owns every rule (evidence floor included) and its verdict comes
    // back in `errors.roomHistory`.
    router.post(
      `${basePath}/rooms/history`,
      {
        room_type_id: room.roomTypeId,
        effective_from: `${year}-${month}-01`,
        total_units: count,
      },
      {
        preserveScroll: true,
        onStart: () => {
          setSubmitting(true)
          setError(null)
        },
        onError: (errors) => setError(errors.roomHistory ?? 'That change could not be saved.'),
        onSuccess: close,
        onFinish: () => setSubmitting(false),
      }
    )
  }

  const complete = month !== '' && year !== '' && count !== ''

  return (
    <Dialog open={room != null} onOpenChange={(open) => !open && close()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Room count change — {room?.roomTypeName}</DialogTitle>
          <DialogDescription>
            Tell us what this room&apos;s bookable count became, and from when.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <Label>From</Label>
            {/* Two Selects, not <input type="month"> — desktop Safari and Firefox don't
                support it, and month is the only precision offered (design D4). */}
            <div className="grid grid-cols-2 gap-2">
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="rounded-lg border-border/60">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_OPTIONS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="rounded-lg border-border/60">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions().map((y) => (
                    <SelectItem key={y} value={y}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="room-history-count">Bookable rooms</Label>
            <Input
              id="room-history-count"
              type="number"
              min={1}
              value={count}
              onChange={(e) => setCount(e.target.value)}
            />
          </div>

          {/* The server's verdict — the evidence floor lands here. */}
          {error && <p className="text-sm text-rose-600">{error}</p>}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" className="rounded-lg" onClick={close}>
            Cancel
          </Button>
          <Button
            type="button"
            className="rounded-lg"
            disabled={!complete || submitting}
            onClick={submit}
          >
            Save change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
